import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, ChevronLeft, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { apiEndpoints, apiRequest, resolveApiAssetUrl } from '@/config/api';
import { BlogPost as BlogPostType, ApiResponse } from '@/types/api';
import SeoHead, { companySchema, SITE_URL } from '@/components/SeoHead';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug]);
  const { data, isLoading, error } = useQuery({
    queryKey: ['blog', slug],
    queryFn: () => apiRequest<ApiResponse<BlogPostType>>(`${apiEndpoints.blogs}/${slug}`),
    enabled: !!slug,
  });

  const post = data?.data;
  const postDescription = post?.excerpt || post?.content?.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300) || 'مقاله تخصصی کیا الکتروموتور پارت';
  const featuredImageUrl = post?.featured_image ? resolveApiAssetUrl(post.featured_image) : null;
  const contentHasFeaturedImage = Boolean(featuredImageUrl && post?.content?.includes(post.featured_image));

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Header />
      <main className="pt-14 sm:pt-16 md:pt-20">
        {isLoading ? (
          <div className="flex justify-center py-32"><Loader2 className="w-10 h-10 animate-spin text-copper" /></div>
        ) : error || !post ? (
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold mb-4">مقاله یافت نشد</h1>
            <Link to="/blog" className="text-copper hover:underline">بازگشت به مقالات</Link>
          </div>
        ) : (
          <>
            <SeoHead
              title={`${post.title} | کیا الکتروموتور پارت`}
              description={postDescription}
              canonicalPath={`/blog/${post.slug}`}
              image={post.featured_image ? (resolveApiAssetUrl(post.featured_image) || '/images/hero-factory-background.png') : '/images/hero-factory-background.png'}
              type="article"
              keywords={['الکتروموتور', 'کیا الکتروموتور', 'راهنمای فنی', post.title]}
              structuredData={[
                { '@context': 'https://schema.org', ...companySchema },
                { '@context': 'https://schema.org', '@type': 'Article', headline: post.title, description: postDescription, image: post.featured_image ? [resolveApiAssetUrl(post.featured_image) || post.featured_image] : undefined, datePublished: post.publish_date || post.created_at, dateModified: post.updated_at, author: { '@id': `${SITE_URL}/#organization` }, publisher: { '@id': `${SITE_URL}/#organization` }, mainEntityOfPage: `${SITE_URL}/blog/${post.slug}` },
              ]}
            />
            <div className="bg-steel-light/30 py-4">
              <div className="container mx-auto px-4 flex items-center gap-2 text-sm">
                <Link to="/" className="text-muted-foreground hover:text-copper">صفحه اصلی</Link>
                <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                <Link to="/blog" className="text-muted-foreground hover:text-copper">مقالات</Link>
                <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                <span className="text-foreground font-medium line-clamp-1">{post.title}</span>
              </div>
            </div>

            <article className="container mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-14 lg:px-8 lg:py-16">
              <header className="mx-auto mb-10 max-w-6xl border-b border-border/70 pb-8 md:mb-14 md:pb-10">
                <h1 className="max-w-5xl text-3xl font-extrabold leading-[1.55] text-foreground sm:text-4xl md:text-5xl">{post.title}</h1>
                <div className="mt-5 flex items-center text-sm font-medium text-muted-foreground"><Calendar className="ml-2 h-4 w-4 text-copper" />{new Date(post.publish_date || post.created_at).toLocaleDateString('fa-IR')}</div>
              </header>
              {featuredImageUrl && !contentHasFeaturedImage && (
                <img src={featuredImageUrl} alt={post.title} width={1200} height={675} className="mx-auto mb-12 max-h-[620px] w-full max-w-6xl rounded-3xl object-cover shadow-xl" />
              )}
              <div dir="rtl" className="article-content mx-auto max-w-6xl" dangerouslySetInnerHTML={{ __html: post.content.replace(/(src=["']\/images\/[^"']+)\.(?:png|jpe?g)(["'])/gi, '$1.webp$2') }} />
            </article>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
